import { useInfiniteQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../../constants'

import { StoresContext } from '../../../provider/StoreProvider'

import { useUserInfo } from '../../../hooks/useUserInfo'

import { getQnaList } from '../../../api/qna'

import { Head } from '../../../components/head/Head'
import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { RowBetween, List, Item, CenterWrap } from '../../../components/common'
import { Button } from '../../../components/button'
import { Footer } from '../../../components/footer'
import { Header } from '../../../components/header'
import { Text, StyledText } from '../../../components/text'
import { DivideLineBold } from '../../../components/divide'
import { Spinner } from '../../../components/spinner/Spinner'
import { InfinityScroll } from '../../../components/pagingList/InfinityScroll'

export const HEAD_INFO = {
  title: '1:1 문의 | 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '1:1 문의 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}

export function Qna() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)

  const { isLogin, isCheckingLogin } = useUserInfo()

  const { data, isPending, isError, hasNextPage, refetch, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEY.qnaList],
    queryFn: async ({ pageParam = 0 }) => await getQnaList({ offset: pageParam, size: 50 }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  useEffect(() => {
    setShowTab(false)
  }, [])

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  if (isPending || isError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" title="1:1문의" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="1:1문의" />
        <Content id="infinity-scroll-target" isPullEvent={true} onPull={refetch}>
          <QnaInfoWrap>
            <Text size={18} weight={600} block={true}>
              운영시간 안내
            </Text>
            <QnaTime size={14} lineHeight={22} color="error" block={true}>
              접수시간: 24시간접수
              <br />
              답변 가능 시간: 월-금 10:00 ~ 17:00
            </QnaTime>
            <Text size={12} color="text_secondary" block={true}>
              답변 가능 시간 이후 접수건은 운영시간 내 순차적으로 답변해 드려요.
            </Text>
          </QnaInfoWrap>
          <DivideLineBold />
          <List>
            <InfinityScroll length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
              {data.pages.map(({ id, title, createdDate, status }) => (
                <Link key={id} to={`/qna/${id}`}>
                  <Item gap={12}>
                    <RowBetween>
                      {status === 'WAIT_FOR_REPLY' ? (
                        <Text size={12} weight={500} color="error">
                          답변 대기
                        </Text>
                      ) : (
                        <Text size={12} weight={500} color="primary_5">
                          답변 완료
                        </Text>
                      )}
                      <Text size={12} color="text_secondary">
                        {dayjs(createdDate).format('YYYY.MM.DD')}
                      </Text>
                    </RowBetween>
                    <Text size={16} weight={500}>
                      {title ?? '-'}
                    </Text>
                  </Item>
                </Link>
              ))}
            </InfinityScroll>
          </List>
        </Content>
        <Footer>
          <Button type="primary" size="large" block={true} href="/qna/register">
            문의하기
          </Button>
        </Footer>
      </Wrap>
    </>
  )
}

const QnaInfoWrap = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 24px 16px;
  font-size: 13px;
`

const QnaTime = styled(StyledText)`
  margin: 16px 0 24px;
`
